import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import I18n from 'utils/I18n';
import { trackV2 } from 'utils/analytics';
import clsx from '@strava/ui/clsx';
import { camelCase, upperFirst } from 'lodash-es';
import { useHistory } from 'react-router-dom';
import styles from './LandingPage.scss';
import AccountSignupForm from '../../components/shared/AccountSignupForm';
import { LANDING_PAGE } from '../../utils/onboarding';
import { LOCALE } from '../../constants/appConstants';
import Header from './components/Header';
import Footer from './components/Footer';

const COLUMN_SIDE = Object.freeze({
  left: 'left',
  right: 'right'
});

const LandingPage = ({ locale, config, authorizationUrls, flashMessage }) => {
  const history = useHistory();

  // default to en-US for all untranslated locales
  const languageAlias = Object.values(LOCALE).includes(locale)
    ? locale
    : LOCALE.enUS;

  useEffect(() => {
    trackV2({
      category: 'onboarding',
      page: 'home',
      action: 'screen_enter',
      properties: {
        source: 'v2',
        locale,
        cohort: 'variant-a'
      }
    });

    // TODO [fumba] - remove v2 param once this goes to prod 100%
    // https://strava.atlassian.net/browse/ACQN-19
    const params = new URLSearchParams(window.location.search);
    if (!params.has('v2')) {
      params.append('v2', true);
    }
    history.replace({
      pathname: window.location.pathname,
      search: params.toString(),
      state: { isActive: true }
    });
  }, [history, locale]);

  // computes the class for side col images
  // eg. derives 'leftImageEnUs' from side - left and locale - en-US
  const bgImgClass = (side) =>
    `${side}Image${upperFirst(camelCase(languageAlias))}`;

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.mainContent}>
        <div
          data-cy="left-column"
          className={clsx(
            styles.sideColumn,
            styles.positionTopRight,
            styles[bgImgClass(COLUMN_SIDE.left)]
          )}
        />
        <div className={styles.middleColumn}>
          <h2>{I18n.t('components.landing_page.heading_v2')}</h2>
          <p>{I18n.t('components.landing_page.description')}</p>
          <AccountSignupForm
            config={config}
            page={LANDING_PAGE}
            authorizationUrls={authorizationUrls}
            flashMessage={flashMessage}
            locale={locale}
            className={styles.signupFormContainer}
          />
        </div>
        <div
          data-cy="right-column"
          className={clsx(
            styles.sideColumn,
            styles.positionTopLeft,
            styles[bgImgClass(COLUMN_SIDE.right)]
          )}
        />
      </div>
      <Footer />
    </div>
  );
};

LandingPage.defaultProps = {
  flashMessage: null,
  locale: LOCALE.enUS
};

LandingPage.propTypes = {
  locale: PropTypes.string,
  config: PropTypes.shape({
    appleEnabled: PropTypes.bool.isRequired,
    appleState: PropTypes.string.isRequired // https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
  }).isRequired,
  authorizationUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    facebook: PropTypes.string.isRequired
  }).isRequired,
  flashMessage: PropTypes.string
};

export default LandingPage;
